import React from 'react';
import { VerticalTabsItemProps } from '../VerticalTabsItem.types';
import { VerticalTabsContextValue } from '../../VerticalTabs/VerticalTabsContext';
import { InjectedFocusableProps } from '../../common/Focusable';
import { st, classes, cssStates } from '../VerticalTabsItem.st.css';
import { Affix } from './Affix';
import { DATA_HOOKS } from '../VerticalTabsItem.constants';
import { Text } from '../..';
import { SIZE } from '../../VerticalTabs/VerticalTabs.constants';

type SelectableItemContainerProps<TabId extends string | number> = Pick<
  InjectedFocusableProps<VerticalTabsItemProps<TabId>, HTMLDivElement>,
  | 'children'
  | 'dataHook'
  | 'prefixIcon'
  | 'suffixIcon'
  | 'skin'
  | 'tabIndex'
  | 'id'
  | 'disabled'
  | 'aria-controls'
  | 'focusableOnBlur'
  | 'focusableOnFocus'
> & {
  baseSkin?: VerticalTabsContextValue['skin'];
  size?: VerticalTabsContextValue['size'];
  onChange?: VerticalTabsContextValue<TabId>['onChange'];
  selected?: boolean;
};

const SelectableItem = ({
  children,
  dataHook,
  prefixIcon,
  suffixIcon,
  size,
  baseSkin,
  disabled,
  id,
  tabIndex,
  skin,
  selected,
  onChange,
  'aria-controls': ariaControls,
  focusableOnFocus,
  focusableOnBlur,
}: SelectableItemContainerProps<number | string>) => {
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Space' || e.key === 'Enter') {
      e.preventDefault();
      if (id !== undefined) {
        onChange?.(id);
      }
    }
  };

  return (
    <div
      className={st(
        classes.wrapper,
        cssStates({
          disabled,
          baseSkin,
          skin,
          selected,
        }),
      )}
      id={String(id)}
      role="tab"
      data-hook={dataHook}
      tabIndex={tabIndex}
      aria-selected={selected}
      aria-disabled={disabled}
      aria-controls={ariaControls}
      onFocus={focusableOnFocus}
      onBlur={focusableOnBlur}
      onClick={!disabled && id !== undefined ? () => onChange?.(id) : undefined}
      onKeyDown={!disabled ? e => onKeyDown(e) : undefined}
    >
      <div
        className={st(
          classes.root,
          classes.selectableItem,
          cssStates({
            disabled,
            size,
            baseSkin,
            skin,
            suffixIcon: !!suffixIcon,
            prefixIcon: !!prefixIcon,
            selected,
          }),
        )}
      >
        <Affix
          dataHook={DATA_HOOKS.ITEM_PREFIX_ICON}
          size={size}
          className={st(classes.prefixIcon)}
        >
          {prefixIcon}
        </Affix>

        <Text
          className={classes.text}
          weight="thin"
          size={size === SIZE.TINY ? SIZE.SMALL : size}
          dataHook={DATA_HOOKS.ITEM_TEXT}
        >
          {children}
        </Text>

        <Affix
          dataHook={DATA_HOOKS.ITEM_SUFFIX_ICON}
          size={size}
          className={classes.suffixIcon}
        >
          {suffixIcon}
        </Affix>
      </div>
    </div>
  );
};

export { SelectableItem };
